import { createPortal } from 'react-dom';
import React, { useState, useEffect, useRef } from 'react';
import { masrafRequestAPI, masrafAPI, referansAPI, notificationAPI } from '../../api/api';
import { useAuth } from '../../context/AuthContext';
import { toISODateOrNull, formatDateForDateInput, formatDateForInput, getTodayISOString } from '../../utils/dateUtils';
import './MasrafForm.css';

// Masraf türleri (alfabetik sıralı, Diğer en altta)
const MASRAF_TURLERI = [
  'BARO PULU',
  'BEDEL ARTIRIM HARCI',
  'BERİLE VERİLEN',
  'BİLİRKİŞİ ÜCRETİ',
  'DAVA AÇILIŞ ÜCRETİ',
  'GELEN ÖDEME',
  'GELEN ÖDEME (İCRA)',
  'GELEN ÖDEME(BAŞVURU AŞAMASI)',
  'GELEN ÖDEME(TAHKİM AŞAMASI)',
  'HARİCEN TAHSİL HARCI',
  'İCRA AÇILIŞ MASRAFLARI',
  'İCRA TAKİP AÇILIŞ HARCI',
  'MÜVEKKİLDEN ALINAN AVUKATLIK ÖDEMESİ',
  'MÜVEKKİLDEN ALINAN DOSYA MASRAFI',
  'MÜVEKKİLDEN ALINAN MASRAF',
  'MÜVEKKİLE YAPILAN ÖDEME',
  'MÜVEKKİLE YAPILAN ÖDEME(BAŞVURU AŞAMASI)',
  'MÜVEKKİLE YAPILAN ÖDEME(İCRA)',
  'MÜVEKKİLE YAPILAN ÖDEME(TAHKİM AŞAMASI)',
  'NOTER MASRAFI',
  'REFERANSA VERİLEN AVANS',
  'TAHKİM BAŞVURU ÜCRETİ VE TEBLİGAT GİDERİ',
  'TAHİL HARCI',
  'TEBLİGAT GİDERİ',
  'TEVKİL',
  'VEKALET HARCI',
  'DİĞER' // Diğer en altta
];

/**
 * Resmi / tarifeye dayalı varsayılan tutarlar (2026).
 * Kaynaklar: Harçlar Kanunu Genel Tebliği (Seri No:98), TBB vekalet pulu,
 * Sigorta Tahkim Komisyonu başvuru/tebligat tarifesi (KEP’siz).
 * Tutarı seçince doldurulur; kullanıcı değiştirebilir.
 */
const MASRAF_DEFAULT_TUTAR = {
  'BARO PULU': 164,
  'VEKALET HARCI': 104,
  'DAVA AÇILIŞ ÜCRETİ': 732, // Asliye / İş / Aile başvurma harcı
  'İCRA TAKİP AÇILIŞ HARCI': 732,
  'TEBLİGAT GİDERİ': 265, // PTT tebligat (yaklaşık / borçlu başı)
  'TAHKİM BAŞVURU ÜCRETİ VE TEBLİGAT GİDERİ': 675,
  'BİLİRKİŞİ ÜCRETİ': 3500, // asgari/tipik; dosyaya göre değişir
};

const normalizeMasrafTuru = (tur) =>
  String(tur || '')
    .trim()
    .toLocaleUpperCase('tr-TR');

/** Gelen ödeme / müvekkilden alınan → gelir; müvekkile ödeme ve harç/gider kalemleri → gider. */
const inferIsGelir = (tur) => {
  const t = normalizeMasrafTuru(tur);
  if (!t) return false;
  if (t.startsWith('GELEN ÖDEME') || t.startsWith('GELEN ODEME')) return true;
  if (t.startsWith('MÜVEKKİLDEN ALINAN') || t.startsWith('MUVEKKILDEN ALINAN')) return true;
  return false;
};

const MasrafForm = ({ masraf, masrafRequest, caseFileId, caseFiles, caseFile, onSubmit, onCancel, hideCaseFileSelection = false, allowMasrafEdit = false }) => {
  const { user } = useAuth();
  // Bugünün tarihini formatla (YYYY-MM-DD)
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [formData, setFormData] = useState({
    caseFileId: caseFileId || null,
    masrafTuru: '',
    customMasrafTuru: '',
    tutar: '',
    tarih: '', // Deprecated, geriye dönük uyumluluk için
    masrafIstenmeTarihi: getTodayDate(), // Default olarak bugünün tarihi
    sonTarih: getTodayDate(), // Oluşturma tarihi; kullanıcı değiştirebilir
    aciklama: '',
    isGelir: false, // Varsayılan gider; tür seçince otomatik gelir/gider atanır
    referansId: null,
    isBerileVerilen: false  // Berile verilen gider (sadece admin)
  });
  const [loading, setLoading] = useState(false);
  const [showCustomMasrafTuru, setShowCustomMasrafTuru] = useState(false);
  const [showReferansSelection, setShowReferansSelection] = useState(false);
  const [referanslar, setReferanslar] = useState([]);

  const isAdmin = () => {
    if (!user) return false;
    const role = user.Role ?? user.role ?? '';
    return role === 'Admin';
  };

  useEffect(() => {
    // Referansları yükle
    const loadReferanslar = async () => {
      try {
        const data = await referansAPI.getAll();
        setReferanslar(data || []);
      } catch (error) {
        console.error('Referanslar yüklenirken hata:', error);
      }
    };
    loadReferanslar();
  }, []);

  /* TESHIS (2026-10-03): "masraf ekle tikliyorum acilmiyor". Uygulamada
     pencere acildiginda ekranda gercekten gorunuyor mu, telefon bir kez
     sunucu kaydina yaziyor: kutunun konumu ve ekranin ortasindaki oge. */
  useEffect(() => {
    if (!document.body.classList.contains('capacitor-native')) return undefined;
    const t = setTimeout(() => {
      try {
        const k = document.querySelector('.masraf-form-container');
        const r = k ? k.getBoundingClientRect() : null;
        const orta = document.elementFromPoint(window.innerWidth / 2, window.innerHeight / 2);
        const gorunur = !!(k && orta && k.contains(orta));
        notificationAPI.tani('masraf-form', r
          ? `gorunur=${gorunur} kutu=${Math.round(r.left)},${Math.round(r.top)} ${Math.round(r.width)}x${Math.round(r.height)} ekran=${window.innerWidth}x${window.innerHeight} ic-ice=${!!document.querySelector('.case-file-form-overlay')} orta=${orta ? orta.className.toString().slice(0, 40) : '-'}`
          : 'kutu yok');
      } catch (e) { /* yoksay */ }
    }, 600);
    return () => clearTimeout(t);
  }, []);

  const formInitKeyRef = useRef(null);

  useEffect(() => {
    const requestId = masrafRequest?.Id ?? masrafRequest?.id;
    const masrafId = masraf?.Id ?? masraf?.id;
    const initKey = requestId != null
      ? `req-${requestId}`
      : masrafId != null
        ? `masraf-${masrafId}`
        : `new-${caseFileId ?? 'none'}`;

    // Parent her render'da yeni caseFiles/caseFile referansı gönderince formu sıfırlama
    if (formInitKeyRef.current === initKey) return;
    formInitKeyRef.current = initKey;

    if (masrafRequest) {
      const masrafTuru = masrafRequest.masrafTuru || masrafRequest.MasrafTuru || '';
      const customMasrafTuru = masrafRequest.customMasrafTuru || masrafRequest.CustomMasrafTuru || '';
      const isCustomType = masrafTuru && !MASRAF_TURLERI.includes(masrafTuru);
      const finalMasrafTuru = isCustomType ? 'DİĞER' : masrafTuru;

      setFormData({
        caseFileId: masrafRequest.caseFileId || masrafRequest.CaseFileId || caseFileId || null,
        masrafTuru: finalMasrafTuru,
        customMasrafTuru: isCustomType ? masrafTuru : customMasrafTuru,
        tutar: masrafRequest.tutar || masrafRequest.Tutar || '',
        tarih: formatDateForDateInput(masrafRequest.tarih || masrafRequest.Tarih),
        masrafIstenmeTarihi: formatDateForDateInput(masrafRequest.masrafIstenmeTarihi || masrafRequest.MasrafIstenmeTarihi || masrafRequest.tarih || masrafRequest.Tarih),
        sonTarih: formatDateForDateInput(masrafRequest.sonTarih || masrafRequest.SonTarih) || getTodayDate(),
        aciklama: masrafRequest.aciklama || masrafRequest.Aciklama || '',
        isGelir: masrafRequest.isGelir ?? masrafRequest.IsGelir ?? inferIsGelir(finalMasrafTuru),
        referansId: null,
        isBerileVerilen: false
      });
      setShowCustomMasrafTuru(isCustomType || masrafTuru === 'DİĞER' || masrafTuru === 'Diğer');
    } else if (masraf) {
      const masrafTuru = masraf.masrafTuru || masraf.MasrafTuru || '';
      const customMasrafTuru = masraf.customMasrafTuru || masraf.CustomMasrafTuru || '';
      const isBerileVerilen = masraf.isBerileVerilen ?? masraf.IsBerileVerilen ?? false;
      const isCustomType = masrafTuru && !MASRAF_TURLERI.includes(masrafTuru);
      const isReferansAvans = masrafTuru === 'REFERANSA VERİLEN AVANS' || masrafTuru === 'Referansa Verilen Avans';
      const finalMasrafTuru = isBerileVerilen && masrafTuru !== 'BERİLE VERİLEN' && masrafTuru !== 'Berile Verilen' ? 'BERİLE VERİLEN' : (isCustomType ? 'DİĞER' : masrafTuru);
      const fileId = masraf.caseFileId || masraf.CaseFileId || caseFileId || null;

      let autoReferansId = masraf.referansId || masraf.ReferansId || null;
      if (isReferansAvans && fileId && !autoReferansId) {
        if (caseFile && (caseFile.referansId || caseFile.ReferansId)) {
          autoReferansId = caseFile.referansId || caseFile.ReferansId;
        } else if (caseFiles && caseFiles.length > 0) {
          const foundFile = caseFiles.find(cf => (cf.id || cf.Id) === fileId);
          if (foundFile && (foundFile.referansId || foundFile.ReferansId)) {
            autoReferansId = foundFile.referansId || foundFile.ReferansId;
          }
        }
      }

      setFormData({
        caseFileId: fileId,
        masrafTuru: finalMasrafTuru,
        customMasrafTuru: isCustomType ? masrafTuru : customMasrafTuru,
        tutar: masraf.tutar || masraf.Tutar || '',
        tarih: formatDateForInput(masraf.tarih || masraf.Tarih),
        masrafIstenmeTarihi: formatDateForInput(masraf.masrafIstenmeTarihi || masraf.MasrafIstenmeTarihi || masraf.tarih || masraf.Tarih),
        sonTarih: formatDateForInput(masraf.sonTarih || masraf.SonTarih) || getTodayDate(),
        aciklama: masraf.aciklama || masraf.Aciklama || '',
        isGelir: (isReferansAvans || isBerileVerilen) ? false : (masraf.isGelir ?? masraf.IsGelir ?? inferIsGelir(finalMasrafTuru)),
        referansId: autoReferansId,
        isBerileVerilen: isBerileVerilen
      });
      setShowCustomMasrafTuru(isCustomType || masrafTuru === 'DİĞER' || masrafTuru === 'Diğer');
      setShowReferansSelection(isReferansAvans && !fileId);
    } else if (caseFileId) {
      setFormData(prev => ({ ...prev, caseFileId }));
    }
  }, [masraf, masrafRequest, caseFileId, caseFile, caseFiles]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    
    if (name === 'masrafTuru') {
      const isReferansAvans = value === 'REFERANSA VERİLEN AVANS' || value === 'Referansa Verilen Avans';
      const isBerileVerilen = value === 'BERİLE VERİLEN' || value === 'Berile Verilen';
      setShowCustomMasrafTuru(value === 'DİĞER' || value === 'Diğer');
      
      // Eğer caseFileId varsa ve dosyanın referansı varsa, otomatik olarak referansId'yi set et
      // Referans seçimi UI'ını sadece caseFileId yoksa göster
      const shouldShowReferansSelection = isReferansAvans && !caseFileId && !formData.caseFileId;
      setShowReferansSelection(shouldShowReferansSelection);
      
      // Dosya referansını otomatik al
      let autoReferansId = null;
      if (isReferansAvans && (caseFileId || formData.caseFileId)) {
        const fileId = caseFileId || formData.caseFileId;
        // caseFile prop'undan veya caseFiles array'inden referansı bul
        if (caseFile && (caseFile.referansId || caseFile.ReferansId)) {
          autoReferansId = caseFile.referansId || caseFile.ReferansId;
        } else if (caseFiles && caseFiles.length > 0) {
          const foundFile = caseFiles.find(cf => (cf.id || cf.Id) === fileId);
          if (foundFile && (foundFile.referansId || foundFile.ReferansId)) {
            autoReferansId = foundFile.referansId || foundFile.ReferansId;
          }
        }
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: value,
        customMasrafTuru: value !== 'DİĞER' && value !== 'Diğer' ? '' : prev.customMasrafTuru,
        referansId: isReferansAvans ? (autoReferansId || prev.referansId) : null,
        isGelir: (isReferansAvans || isBerileVerilen) ? false : inferIsGelir(value),
        isBerileVerilen: isBerileVerilen, // Berile Verilen seçildiğinde true
        // Tarifesi bilinen türlerde tutarı otomatik doldur (elle değiştirilebilir)
        tutar: (MASRAF_DEFAULT_TUTAR[value] != null)
          ? String(MASRAF_DEFAULT_TUTAR[value])
          : prev.tutar
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const finalCaseFileId = caseFileId || formData.caseFileId;
    
    if (!finalCaseFileId && !hideCaseFileSelection) {
      alert('Lütfen bir dosya seçin!');
      return;
    }

    if (!formData.masrafTuru) {
      alert('Lütfen masraf türü seçin!');
      return;
    }

    if ((formData.masrafTuru === 'DİĞER' || formData.masrafTuru === 'Diğer') && !formData.customMasrafTuru.trim()) {
      alert('Lütfen masraf türü açıklaması girin!');
      return;
    }

    if (!formData.tutar || parseFloat(formData.tutar) <= 0) {
      alert('Lütfen geçerli bir tutar girin!');
      return;
    }

    if (!formData.sonTarih) {
      alert('Lütfen son tarih seçin!');
      return;
    }

    // Referansa Verilen Avans seçildiyse referans zorunlu (sadece caseFileId yoksa)
    const isReferansAvans = formData.masrafTuru === 'REFERANSA VERİLEN AVANS' || formData.masrafTuru === 'Referansa Verilen Avans';
    if (isReferansAvans && !formData.referansId && !finalCaseFileId) {
      alert('Lütfen bir referans seçin!');
      return;
    }
    
    // Dosya referansını otomatik al (eğer caseFileId varsa)
    let finalReferansId = formData.referansId;
    if (isReferansAvans && finalCaseFileId && !finalReferansId) {
      if (caseFile && (caseFile.referansId || caseFile.ReferansId)) {
        finalReferansId = caseFile.referansId || caseFile.ReferansId;
      } else if (caseFiles && caseFiles.length > 0) {
        const foundFile = caseFiles.find(cf => (cf.id || cf.Id) === finalCaseFileId);
        if (foundFile && (foundFile.referansId || foundFile.ReferansId)) {
          finalReferansId = foundFile.referansId || foundFile.ReferansId;
        }
      }
      
      if (!finalReferansId) {
        alert('Bu dosyanın referansı bulunamadı!');
        return;
      }
    }

    // hideCaseFileSelection true ise (yeni dosya oluştururken) API'ye istek atma
    if (hideCaseFileSelection) {
      const isBerileVerilen = formData.masrafTuru === 'BERİLE VERİLEN' || formData.masrafTuru === 'Berile Verilen';
      const isDiğer = formData.masrafTuru === 'DİĞER' || formData.masrafTuru === 'Diğer';
      const sonTarihISO = toISODateOrNull(formData.sonTarih);
      const masrafIstenmeTarihiISO = toISODateOrNull(formData.masrafIstenmeTarihi);
      onSubmit({
        MasrafTuru: formData.masrafTuru,
        CustomMasrafTuru: isDiğer ? formData.customMasrafTuru : null,
        Tutar: parseFloat(formData.tutar),
        Tarih: sonTarihISO, // Son Tarih değeri Tarih olarak kaydedilecek
        MasrafIstenmeTarihi: masrafIstenmeTarihiISO,
        SonTarih: sonTarihISO,
        Aciklama: formData.aciklama || null,
        IsGelir: (isReferansAvans || isBerileVerilen) ? false : formData.isGelir,
        ReferansId: isReferansAvans ? (finalReferansId ? parseInt(finalReferansId) : null) : null,
        IsBerileVerilen: isBerileVerilen
      });
      return;
    }

    setLoading(true);
    try {
      // Mevcut bir masrafı düzenleme yetkisi olan kullanıcı (ör. baran/beril) doğrudan günceller.
      const directEdit = !!masraf && allowMasrafEdit;
      // Avukatlar masraf talebi oluşturur; admin onaylayınca masraf dosyaya işlenir.
      // Ancak doğrudan düzenleme yetkisi varsa talep değil, güncelleme yapılır.
      if (!isAdmin() && !directEdit) {
        if (!formData.sonTarih) {
          alert('Lütfen son tarih seçin!');
          setLoading(false);
          return;
        }
        const sonTarihISO = toISODateOrNull(formData.sonTarih);
        const masrafIstenmeTarihiISO = toISODateOrNull(formData.masrafIstenmeTarihi);
        const isDiğer = formData.masrafTuru === 'DİĞER' || formData.masrafTuru === 'Diğer';
        const createDto = {
          CaseFileId: parseInt(finalCaseFileId),
          MasrafTuru: isDiğer ? 'Diğer' : formData.masrafTuru,
          CustomMasrafTuru: isDiğer ? formData.customMasrafTuru : null,
          Tutar: parseFloat(formData.tutar),
          Tarih: sonTarihISO,
          MasrafIstenmeTarihi: masrafIstenmeTarihiISO,
          SonTarih: sonTarihISO,
          Aciklama: formData.aciklama || null,
          IsGelir: (isReferansAvans || formData.masrafTuru === 'BERİLE VERİLEN' || formData.masrafTuru === 'Berile Verilen') ? false : formData.isGelir
        };
        await masrafRequestAPI.create(createDto);
        alert('Masraf talebiniz gönderildi. Admin onayından sonra dosyaya işlenecektir.');
        onSubmit();
        return;
      }

      // Admin: direkt masraf oluştur veya güncelle
      {
        // Admin ise direkt masraf oluştur
        // Son Tarih zorunlu kontrolü
        if (!formData.sonTarih) {
          alert('Lütfen son tarih seçin!');
          setLoading(false);
          return;
        }
        
        const sonTarihISO = toISODateOrNull(formData.sonTarih);
        const masrafIstenmeTarihiISO = toISODateOrNull(formData.masrafIstenmeTarihi);
        const isBerileVerilen = formData.masrafTuru === 'BERİLE VERİLEN' || formData.masrafTuru === 'Berile Verilen';
        const isDiğer = formData.masrafTuru === 'DİĞER' || formData.masrafTuru === 'Diğer';
        const masrafData = {
          CaseFileId: parseInt(finalCaseFileId),
          MasrafTuru: isDiğer ? formData.customMasrafTuru : formData.masrafTuru,
          CustomMasrafTuru: isDiğer ? formData.customMasrafTuru : null,
          Tutar: parseFloat(formData.tutar),
          Tarih: sonTarihISO, // Son Tarih değeri Tarih olarak kaydedilecek
          MasrafIstenmeTarihi: masrafIstenmeTarihiISO,
          SonTarih: sonTarihISO,
          Aciklama: formData.aciklama || null,
          IsBerileVerilen: isBerileVerilen,  // Berile Verilen seçildiğinde true
          IsGelir: (isReferansAvans || isBerileVerilen) ? false : formData.isGelir,
          ReferansId: isReferansAvans ? (finalReferansId ? parseInt(finalReferansId) : null) : null
        };

        if (masraf) {
          // Masraf güncelleme
          const updateData = {
            Id: masraf.id || masraf.Id,
            CaseFileId: parseInt(finalCaseFileId),
            MasrafTuru: isDiğer ? formData.customMasrafTuru : formData.masrafTuru,
            CustomMasrafTuru: isDiğer ? formData.customMasrafTuru : null,
            Tutar: parseFloat(formData.tutar),
            Tarih: sonTarihISO, // Son Tarih değeri Tarih olarak kaydedilecek
            MasrafIstenmeTarihi: masrafIstenmeTarihiISO,
            SonTarih: sonTarihISO,
            Aciklama: formData.aciklama || null,
            IsGelir: (isReferansAvans || isBerileVerilen) ? false : formData.isGelir,
            ReferansId: isReferansAvans ? (finalReferansId ? parseInt(finalReferansId) : null) : null,
            IsBerileVerilen: isBerileVerilen  // Berile Verilen seçildiğinde true
          };
          await masrafAPI.update(updateData);
          alert('Masraf başarıyla güncellendi.');
        } else {
          // Admin direkt masraf oluştur
          await masrafAPI.create(masrafData);
          alert('Masraf başarıyla oluşturuldu.');
        }
        onSubmit();
      }
    } catch (error) {
      console.error('Masraf kaydedilirken hata:', error);
      alert('Masraf kaydedilirken hata oluştu: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  /* PENCERE HER ZAMAN EN UST KATMANDA (document.body'ye tasinir).
     Bu form baska bir pencerenin icinden de aciliyor (yeni dosya formu ->
     "Masraf Ekle" / "Not Ekle"). Ic ice kalinca, distaki pencere hareketli
     (transform) oldugunda "position: fixed" ekrana degil o pencereye gore
     yerlesiyor ve form gorunmez kalabiliyordu (2026-10-03: "masraf ekle
     tikliyorum acilmiyor"). Olaylar React agacinda eskisi gibi akar. */
  return createPortal(
    <div className="masraf-form-overlay" onClick={(e) => {
      if (e.target.classList.contains('masraf-form-overlay')) {
        onCancel();
      }
    }}>
      <div className="masraf-form-container" onClick={(e) => e.stopPropagation()}>
        <div className="form-header">
          <h2>{masraf ? 'Masraf Düzenle' : (isAdmin() ? 'Yeni Masraf Ekle' : 'Masraf Talebi Oluştur')}</h2>
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            ✕
          </button>
        </div>

        <form 
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit(e);
          }}
          className="masraf-form"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
        >
          {/* Dosya seçimi */}
          {!caseFileId && !hideCaseFileSelection && (
            <div className="form-group">
              <label>Dosya *</label>
              <select
                name="caseFileId"
                value={formData.caseFileId || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, caseFileId: e.target.value }))}
                required
              >
                <option value="">Dosya Seçin</option>
                {[...(caseFiles || [])]
                  .sort((a, b) => {
                    const foyA = String(a.foyNo ?? a.FoyNo ?? '');
                    const foyB = String(b.foyNo ?? b.FoyNo ?? '');
                    return foyA.localeCompare(foyB, 'tr', { numeric: true });
                  })
                  .map(cf => (
                    <option key={cf.id || cf.Id} value={cf.id || cf.Id}>
                      Föy No: {cf.foyNo || cf.FoyNo} - {cf.muvekkil || cf.Muvekkil}
                    </option>
                  ))}
              </select>
            </div>
          )}

          {/* Masraf Türü */}
          <div className="form-group">
            <label>Masraf Türü *</label>
            <select
              name="masrafTuru"
              value={formData.masrafTuru}
              onChange={handleChange}
              required
            >
              <option value="">Masraf Türü Seçin</option>
              {MASRAF_TURLERI.filter(tur => {
                // BERİLE VERİLEN ve REFERANSA VERİLEN AVANS sadece adminlerde görünsün
                if (tur === 'BERİLE VERİLEN' || tur === 'REFERANSA VERİLEN AVANS') {
                  return isAdmin();
                }
                return true;
              }).map(tur => (
                <option key={tur} value={tur}>{tur}</option>
              ))}
            </select>
          </div>

          {/* Diğer seçilirse masraf türü açıklaması */}
          {showCustomMasrafTuru && (
            <div className="form-group">
              <label>Masraf Türü Açıklaması *</label>
              <input
                type="text"
                name="customMasrafTuru"
                value={formData.customMasrafTuru}
                onChange={handleChange}
                placeholder="Masraf türünü yazın"
                required
              />
            </div>
          )}

          {/* REFERANSA VERİLEN AVANS seçilirse referans seçimi (sadece caseFileId yoksa) */}
          {showReferansSelection && isAdmin() && !caseFileId && !formData.caseFileId && (
            <div className="form-group">
              <label>Referans *</label>
              <select
                name="referansId"
                value={formData.referansId || ''}
                onChange={handleChange}
                required
              >
                <option value="">Referans Seçin</option>
                {referanslar.filter(r => r.isActive !== false).map(referans => (
                  <option key={referans.id || referans.Id} value={referans.id || referans.Id}>
                    {referans.name || referans.Name}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {/* Dosya referansı bilgisi (caseFileId varsa ve referans otomatik alındıysa) */}
          {(formData.masrafTuru === 'REFERANSA VERİLEN AVANS' || formData.masrafTuru === 'Referansa Verilen Avans') && (caseFileId || formData.caseFileId) && formData.referansId && (
            <div className="form-group" style={{ padding: '10px', backgroundColor: '#e7f3ff', borderRadius: '4px', marginBottom: '15px' }}>
              <label style={{ fontWeight: 'bold', color: '#0066cc' }}>Referans:</label>
              <span style={{ marginLeft: '10px', color: '#0066cc' }}>
                {(() => {
                  // Önce caseFile'dan ReferansAdi'yi kontrol et
                  if (caseFile && (caseFile.ReferansAdi || caseFile.referansAdi)) {
                    return caseFile.ReferansAdi || caseFile.referansAdi;
                  }
                  // Yoksa caseFiles array'inden kontrol et
                  const fileId = caseFileId || formData.caseFileId;
                  if (caseFiles && caseFiles.length > 0) {
                    const foundFile = caseFiles.find(cf => (cf.id || cf.Id) === fileId);
                    if (foundFile && (foundFile.ReferansAdi || foundFile.referansAdi)) {
                      return foundFile.ReferansAdi || foundFile.referansAdi;
                    }
                  }
                  // Son olarak referanslar listesinden bul
                  const referansId = formData.referansId;
                  if (referansId && referanslar.length > 0) {
                    const foundReferans = referanslar.find(r => (r.id || r.Id) === referansId);
                    if (foundReferans) {
                      return foundReferans.name || foundReferans.Name;
                    }
                  }
                  // Eğer referanslar henüz yüklenmediyse
                  return referanslar.length === 0 ? 'Yükleniyor...' : 'Referans bulunamadı';
                })()}
              </span>
              <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#666' }}>
                Bu dosyanın referansına otomatik olarak eklenecektir.
              </p>
            </div>
          )}

          {/* Gelir/Gider seçimi */}
          <div className="form-group">
            <label>Gelir / Gider *</label>
            {(formData.masrafTuru === 'REFERANSA VERİLEN AVANS' || formData.masrafTuru === 'Referansa Verilen Avans') ? (
              <div style={{ padding: '10px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
                <span style={{ fontWeight: 'bold', color: '#856404' }}>Gider (REFERANSA VERİLEN AVANS her zaman gider olarak kaydedilir)</span>
              </div>
            ) : (isAdmin() && (formData.masrafTuru === 'BERİLE VERİLEN' || formData.masrafTuru === 'Berile Verilen')) ? (
              <div style={{ padding: '10px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
                <span style={{ fontWeight: 'bold', color: '#856404' }}>Gider (BERİLE VERİLEN her zaman gider olarak kaydedilir)</span>
              </div>
            ) : (
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="isGelir"
                    value="true"
                    checked={formData.isGelir === true}
                    onChange={(e) => setFormData(prev => ({ ...prev, isGelir: true, isBerileVerilen: false }))}
                  />
                  <span>Gelir</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="isGelir"
                    value="false"
                    checked={formData.isGelir === false}
                    onChange={(e) => setFormData(prev => ({ ...prev, isGelir: false }))}
                  />
                  <span>Gider</span>
                </label>
              </div>
            )}
            {!(formData.masrafTuru === 'REFERANSA VERİLEN AVANS' || formData.masrafTuru === 'Referansa Verilen Avans')
              && !(isAdmin() && (formData.masrafTuru === 'BERİLE VERİLEN' || formData.masrafTuru === 'Berile Verilen')) && (
              <small className="form-text text-muted">
                Tür seçilince otomatik gelir/gider atanır; isterseniz değiştirebilirsiniz.
              </small>
            )}
          </div>

          {/* Tutar */}
          <div className="form-group">
            <label>Masraf Tutarı *</label>
            <input
              type="number"
              name="tutar"
              value={formData.tutar}
              onChange={handleChange}
              step="0.01"
              min="0"
              placeholder="0.00"
              required
            />
          </div>

          {/* Masraf İstenme Tarihi */}
          <div className="form-group">
            <label>Masraf İstenme Tarihi *</label>
            <input
              type="date"
              name="masrafIstenmeTarihi"
              value={formData.masrafIstenmeTarihi}
              onChange={handleChange}
              required
            />
          </div>

          {/* Son Tarih - Zorunlu, Tarih olarak kaydedilecek */}
          <div className="form-group">
            <label>Son Tarih *</label>
            <input
              type="date"
              name="sonTarih"
              value={formData.sonTarih}
              onChange={handleChange}
              required
            />
            <small className="form-text text-muted">
              Varsayılan olarak bugünün (oluşturma) tarihidir; değiştirebilirsiniz. Masraflar listesinde bu tarih görünür.
            </small>
          </div>

          {/* Açıklama */}
          <div className="form-group">
            <label>Açıklama</label>
            <textarea
              name="aciklama"
              value={formData.aciklama}
              onChange={handleChange}
              rows="4"
              placeholder="Açıklama (opsiyonel)"
            />
          </div>

          <div className="form-actions">
            <button 
              type="submit" 
              className="btn btn-primary" 
              disabled={loading}
            >
              {loading 
                ? 'Kaydediliyor...' 
                : (masraf 
                  ? 'Güncelle' 
                  : (isAdmin() 
                    ? 'Masraf Ekle' 
                    : 'Masraf Talebi Gönder'))}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>
              İptal
            </button>
          </div>
        </form>
      </div>
    </div>
  , document.body);
};

export default MasrafForm;
